import type { ReactNode } from 'react';

import { Icon, type IconName } from './Icon.js';
import styles from './Menu.module.css';

/**
 * Dropdown popover for a trigger/split button. Positioned absolutely inside a
 * `position: relative` group; the caller owns the open/dismiss state (typically
 * a `useState` + `useDismiss`) and renders this only while open. A click
 * anywhere inside fires `onClose`, so items don't each have to close the menu —
 * their `onSelect` is just the action.
 */
export function Menu({
  onClose,
  placement = 'down',
  children,
}: {
  onClose: () => void;
  /** Which way it opens from its trigger. A menu near the foot of a column has
   *  to open upward, and that is the menu's own business — a caller overriding
   *  `top` from its own stylesheet is a specificity duel decided by bundle
   *  order. */
  placement?: 'down' | 'up';
  children: ReactNode;
}) {
  return (
    <div
      className={placement === 'up' ? `${styles.menu} ${styles.menuUp}` : styles.menu}
      role="menu"
      onClick={onClose}
    >
      {children}
    </div>
  );
}

/** One selectable row. `danger` tints it destructive-red; icons render outline
 *  (a menu row is never a filled glyph). `disabled` greys it and, being a native
 *  disabled button, swallows the click — so it never fires `onSelect` nor the
 *  menu's own close-on-click, which is why a zero-target row leaves the menu up. */
export function MenuItem({
  icon,
  danger,
  disabled,
  opensPage,
  onSelect,
  children,
}: {
  icon: IconName;
  danger?: boolean;
  disabled?: boolean;
  /** This row's act is carried out on the full chat page, not where the menu is
   *  — the widget's Details and End & reject, today. Marked with a trailing
   *  arrow so a press that takes the employer off the page they're on says so
   *  before they make it, not after. */
  opensPage?: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={danger ? `${styles.item} ${styles.itemDanger}` : styles.item}
      disabled={disabled}
      onClick={onSelect}
    >
      <Icon name={icon} size={16} filled={false} />
      <span className={styles.itemLabel}>{children}</span>
      {opensPage && (
        <span className={styles.itemOpens}>
          <Icon name="arrowUpRight" size={14} />
          {/* `Icon` is always aria-hidden, so the arrow alone says nothing to a
              screen reader — and these labels are identical to /chat's, where
              the same rows act in place. Without this, the only warning that a
              press leaves the page is one nobody hears. */}
          <span className={styles.srOnly}>Opens the full chat page</span>
        </span>
      )}
    </button>
  );
}

export function MenuSeparator() {
  return <div className={styles.separator} />;
}

/** A non-interactive heading over a group of related rows (e.g. "For job messages
 *  (12)"). Read as governing the rows beneath it — which is why it heads them
 *  rather than trailing them. */
export function MenuGroupTitle({ children }: { children: ReactNode }) {
  return (
    <div className={styles.groupTitle} role="presentation">
      {children}
    </div>
  );
}
